import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import SaveButton from '@ui-elements/SaveButton';
import BasicModal from '@ui-parts/BasicModal';
import ResidenceCheckBox from '@ui-parts/ResidenceCheckBox';

const BodyWrapper = styled.div`
  padding: 0 15px;
`;

function RegionModal({ open, areas, checkedAreaCdsProp, handleClose }) {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  // 親エリアのareaCdをキー、子エリアのareaCdの配列をvalueとするobjectを作成
  const areaCdsByParent = {};
  areas.forEach((parentArea) => {
    areaCdsByParent[parentArea.areaCd] = parentArea.children.map((child) => child.areaCd);
  });

  const [checkedAreaCds, setCheckedAreaCds] = useState([...checkedAreaCdsProp]);
  const [isAllChecked, setIsAllChecked] = useState(false);
  const [isAllCheckedByParent, setIsAllCheckedByParent] = useState({});

  const createIsAllChecked = useCallback(
    (_areas) => {
      const initialIsAllCheckedByParent = {};
      _areas.forEach((parentArea) => {
        // 一つでもチェックされてない子エリアがあれば、その親エリアのcheck状態はfalse
        initialIsAllCheckedByParent[parentArea.areaCd] = !parentArea.children.some(
          (child) => !checkedAreaCds.includes(child.areaCd)
        );
      });
      const initialIsAllChecked = !Object.keys(initialIsAllCheckedByParent).some(
        (areaCd) => !initialIsAllCheckedByParent[areaCd]
      );

      return [initialIsAllChecked, initialIsAllCheckedByParent];
    },
    [checkedAreaCds]
  );

  useEffect(() => {
    setCheckedAreaCds([...checkedAreaCdsProp]);
  }, [checkedAreaCdsProp]);

  useEffect(() => {
    const [newIsAllChecked, newIsAllCheckedByParent] = createIsAllChecked(areas);
    setIsAllChecked(newIsAllChecked);
    setIsAllCheckedByParent(newIsAllCheckedByParent);
  }, [areas, checkedAreaCds]);

  const clearCheckedAreaCds = () => {
    setCheckedAreaCds([]);
  };

  const checkAllAreas = () => {
    const allChoices = [];
    areas.forEach((area) => {
      area.children.forEach((child) => {
        allChoices.push(child.areaCd);
      });
    });

    setCheckedAreaCds(allChoices);
  };

  const checkAllAreasByParent = (parentCd) => {
    const allChoices = [...checkedAreaCds];
    areas.forEach((area) => {
      if (area.areaCd === parentCd) {
        area.children.forEach((child) => {
          allChoices.push(child.areaCd);
        });
      }
    });

    // 重複を排除してsort
    const newCheckedAreaCds = Array.from(new Set(allChoices)).sort();
    setCheckedAreaCds(newCheckedAreaCds);
  };

  const uncheckAllByParent = (parentCd) => {
    let allChoices = checkedAreaCds;
    areas.forEach((area) => {
      if (area.areaCd === parentCd) {
        area.children.forEach((child) => {
          allChoices = allChoices.filter((choice) => choice !== child.areaCd);
        });
      }
    });
    setCheckedAreaCds(allChoices);
  };

  const handleChoiceChange = (e) => {
    const targetParentCd = e.target.value.split('-')[0];
    const targetChildCd = e.target.value.split('-')[1];

    if (!e.target.checked) {
      // when unchecked
      setCheckedAreaCds(checkedAreaCds.filter((item) => item !== targetChildCd));
      setIsAllCheckedByParent({ ...isAllCheckedByParent, [targetParentCd]: false });
      setIsAllChecked(false);
      return;
    }

    // when checked
    setCheckedAreaCds([...checkedAreaCds, targetChildCd].sort());

    // ひとつでもチェックされてない子エリアがあればfalse
    const newIsAllCheckedByParent = {
      ...isAllCheckedByParent,
      [targetParentCd]: !areaCdsByParent[targetParentCd].some(
        (areaCd) => ![...checkedAreaCds, targetChildCd].includes(areaCd)
      ),
    };

    // ひとつでも全選択されていない親エリアがあればfalse
    const newIsAllChecked = !Object.keys(newIsAllCheckedByParent).some(
      (parentCd) => !newIsAllCheckedByParent[parentCd]
    );

    setIsAllCheckedByParent(newIsAllCheckedByParent);
    setIsAllChecked(newIsAllChecked);
  };

  const handleCheckAllChange = (e) => {
    // 対象が全選択のとき
    if (e.target.value === 'all') {
      if (e.target.checked) {
        // 全選択をチェック状態をtrueに変更
        setIsAllChecked(true);
        // 親エリアの選択状態を全てtrueに変更
        setIsAllCheckedByParent(
          Object.fromEntries(Object.entries(isAllCheckedByParent).map((entry) => [entry[0], true]))
        );
        // 全子エリアを選択
        checkAllAreas();
      } else {
        // 全選択をチェック状態をfalseに変更
        setIsAllChecked(false);
        // 親エリアの選択状態を全てfalseに変更
        setIsAllCheckedByParent(
          Object.fromEntries(Object.entries(isAllCheckedByParent).map((entry) => [entry[0], false]))
        );
        // 全子エリアの選択をはずす
        clearCheckedAreaCds();
      }
      return true;
    }

    // 対象が親エリアの時
    const parentAreaCd = e.target.value;
    if (e.target.checked) {
      // 対象の親エリアの選択状態をfalseに変更。
      const newIsAllCheckedByParent = { ...isAllCheckedByParent, [parentAreaCd]: true };
      // ひとつでも全選択されていない親エリアがあればfalse
      const newIsAllChecked = !Object.keys(newIsAllCheckedByParent).some(
        (parentCd) => !newIsAllCheckedByParent[parentCd]
      );
      setIsAllCheckedByParent(newIsAllCheckedByParent);
      setIsAllChecked(newIsAllChecked);
      checkAllAreasByParent(parentAreaCd);
    } else {
      setIsAllCheckedByParent({ ...isAllCheckedByParent, [parentAreaCd]: false });
      // 全選択をチェック状態をfalseに変更
      setIsAllChecked(false);
      uncheckAllByParent(parentAreaCd);
    }
    return true;
  };

  return (
    <BasicModal
      open={open}
      titleProps={{
        titleLabel: t('quotas.deliveryTarget'),
        title: t('quotas.conditions.titles.region'),
        desc: t('quotas.modals.region.desc'),
      }}
      handleClose={() => {
        handleClose();
        // 選択状態を元に戻してあげる
        setCheckedAreaCds(checkedAreaCdsProp);
      }}
      buttons={[
        <SaveButton
          key="button1"
          sx={{ width: 148 }}
          handleClick={() => {
            dispatch({
              type: 'qualifications/changed',
              payload: {
                key: 'areas',
                value: {
                  checkedAreaCds,
                },
              },
            });
            handleClose();
          }}
          label={t('appCommon.setting')}
        />,
      ]}
      dataTestId="RegionModal"
    >
      <BodyWrapper>
        <ResidenceCheckBox
          handleChoiceChange={handleChoiceChange}
          handleCheckAllChange={handleCheckAllChange}
          areas={areas}
          checkedChoices={checkedAreaCds}
          isAllChecked={isAllChecked}
          isAllCheckedByParent={isAllCheckedByParent}
        />
      </BodyWrapper>
    </BasicModal>
  );
}

RegionModal.propTypes = {
  open: PropTypes.bool,
  // ↓i動的なのでいったんlintエラーは無視してobjectで定義
  // eslint-disable-next-line react/forbid-prop-types
  areas: PropTypes.arrayOf(PropTypes.object).isRequired,
  checkedAreaCdsProp: PropTypes.arrayOf(PropTypes.string).isRequired,
  handleClose: PropTypes.func,
};

export default RegionModal;
